import { type FC, type ReactNode, useCallback, useEffect, useState } from "react";
import CartContainsItemsModal from "~/features/orders/orderDetails/components/AddToCart/CartContainsItemsModal";
import DeleteCartConfirmationModal from "~/domains/cart/components/DeleteCartConfirmationModal";
import Modal from "~/common/components/Modal";
import Spinner from "~/common/components/Spinner/Spinner";
import { type CustomDispatch } from "~/app/store/types";
import { connect } from "react-redux";
import { addProductsFromOrder, loadSelectedProductData } from "~/features/cart/cart/redux/actions/orderDetailsActions";
import { type AddToCartItem } from "~/features/cart/cart/api/AddToCart";

import { Button, Stack } from "~/common/mui";
import { IconType } from "~/common/components/icon/IconType";
import { selectIsCartEmpty } from "~/features/addToCart/redux/cartAmounts/cartAmountSelectors";
import { type RootState } from "~/app/store";
import { CART_PAGE } from "~/utils/url/staticUrls";
import { type OrderLineItemData } from "~/domains/orders/types/modelsV2";
import { useTranslation } from "react-i18next";
import { openModal } from "~/common/utils/ModalService";
import CartSuccessModal from "~/features/addToCart/components/AddToCartProcess/Modals/CartSuccessModal/CartSuccessModal";
import { DefaultButtonIcon } from "~/common/components/icon/Icon";
import { Alert } from "~/common/mui/Alert/Alert";

interface OwnProps {
  onClose: () => void;
  selectedOrderItems: OrderLineItemData[];
}

interface FromStateProps {
  state: RootState;
  isCartLoaded: boolean;
  isCartEmpty: boolean;
}

interface DispatchProps {
  onMount: () => void;
  onAdd: (override?: boolean) => Promise<unknown>;
}

type Props = OwnProps & FromStateProps & DispatchProps;

enum Step {
  LOADING,
  HAS_CART,
  CONFIRM_OVERRIDE,
  ADDING_DATA,
  SUCCESS,
  ERROR,
}

const OrderDetailsAddToCartFlow: FC<Props> = (props) => {
  const { onClose, isCartLoaded, isCartEmpty, onMount, onAdd, selectedOrderItems } = props;
  const { t } = useTranslation();
  const [step, setStep] = useState<Step>(isCartLoaded && !isCartEmpty ? Step.HAS_CART : Step.LOADING);

  const addCb = useCallback(
    async (override?: boolean) => {
      setStep(Step.ADDING_DATA);
      try {
        const formData = await onAdd(override);
        openModal("add-to-cart-dialog", (close) => (
          <CartSuccessModal formData={formData as AddToCartItem[]} onClose={close} page="ORDER_DETAILS" />
        ));
        onClose();
        //setStep(Step.SUCCESS);
      } catch (e) {
        console.log(e);
        setStep(Step.ERROR);
      }
    },
    [onAdd, props.state.products],
  );

  useEffect(() => {
    if (step === Step.LOADING && isCartLoaded) {
      if (isCartEmpty) {
        addCb(false);
      } else {
        setStep(Step.HAS_CART);
      }
    }
  });

  useEffect(onMount, []);

  const successCount = selectedOrderItems.map((item) => item.qty).reduce((acc, qty) => acc + qty);
  const cartPageUrl = CART_PAGE;

  return (
    <>
      {step === Step.HAS_CART && (
        <CartContainsItemsModal
          onCancel={onClose}
          onOverride={() => setStep(Step.CONFIRM_OVERRIDE)}
          onAdd={() => addCb(false)}
        />
      )}
      {step === Step.CONFIRM_OVERRIDE && (
        <DeleteCartConfirmationModal onCancel={() => setStep(Step.HAS_CART)} onOk={() => addCb(true)} />
      )}
      {(step === Step.LOADING || step === Step.ADDING_DATA) && (
        <Modal onClose={onClose}>
          <Spinner className={"mb-30"} />
        </Modal>
      )}
      {step === Step.SUCCESS && (
        <Modal
          onClose={onClose}
          footerChildren={
            <Stack direction={"row"} gap={1}>
              {getCloseBtn(t("close"), onClose)}
              <div data-test-id={"orders_addtocart_gotocart"}>
                {getActionBtn(t("addToCartPopup.checkoutBtn"), cartPageUrl)}
              </div>
            </Stack>
          }
        >
          <Alert className="mb-30" severity={"info"}>
            {t("wishlistPage.globalMsg.addedToCart", { count: successCount })}
          </Alert>
        </Modal>
      )}
      {step === Step.ERROR && (
        <Modal
          onClose={onClose}
          footerChildren={
            <Stack direction={"row"} gap={1}>
              {getCloseBtn(t("close"), onClose)}
              {getActionBtn(t("addToCartPopup.checkoutBtn"), cartPageUrl, true)}
            </Stack>
          }
        >
          <Alert className="mb-30" severity={"error"}>
            {t(`addToCartPopup.add.error`)}
          </Alert>
        </Modal>
      )}
    </>
  );
};

const mapStateToProps = (state: RootState, ownProps: OwnProps): FromStateProps => {
  const isCartLoaded = !state.cartPage.loading && !state.cartPage.error;
  const isCartEmpty = selectIsCartEmpty(state);

  return {
    state,
    isCartLoaded,
    isCartEmpty,
  };
};

const mapDispatchToProps = (dispatch: CustomDispatch<RootState>, ownProps: OwnProps): DispatchProps => {
  return {
    onMount: () => dispatch(loadSelectedProductData(ownProps.selectedOrderItems)),
    onAdd: (override?: boolean) => dispatch(addProductsFromOrder(ownProps.selectedOrderItems, override)),
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(OrderDetailsAddToCartFlow);

function getCloseBtn(label: ReactNode, onClose: () => void) {
  return <Button onClick={onClose}>{label}</Button>;
}

type Func = () => void;
function getActionBtn(label: ReactNode, action: string | Func, disabled?: boolean) {
  const props = {
    disabled: window.SCHA.config.disableAllWS || disabled,
    href: typeof action === "string" ? action : undefined,
    onClick: typeof action === "function" ? action : undefined,
  };
  return (
    <Button startIcon={<DefaultButtonIcon type={IconType.Cart} />} {...props}>
      {label}
    </Button>
  );
}
